import { motion } from "framer-motion";
import { Users, Crown, UserPlus, TrendingDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { UserAnalyticsResponse } from "@/hooks/use-users";

interface UserStatsCardsProps {
  analytics: UserAnalyticsResponse | undefined;
  isLoading: boolean;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: {
    value: number;
    label: string;
    isPositive: boolean;
  };
  color: "blue" | "amber" | "emerald" | "rose";
  delay: number;
}

const colorVariants = {
  blue: {
    bg: "bg-blue-500/10",
    icon: "text-blue-500",
    border: "border-blue-500/20",
  },
  amber: {
    bg: "bg-amber-500/10",
    icon: "text-amber-500",
    border: "border-amber-500/20",
  },
  emerald: {
    bg: "bg-emerald-500/10",
    icon: "text-emerald-500",
    border: "border-emerald-500/20",
  },
  rose: {
    bg: "bg-rose-500/10",
    icon: "text-rose-500",
    border: "border-rose-500/20",
  },
};

function StatCard({ title, value, icon, trend, color, delay }: StatCardProps) {
  const colors = colorVariants[color];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
    >
      <Card className={cn("border", colors.border)}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">{title}</p>
              <p className="text-3xl font-bold tracking-tight">{value}</p>
              {trend ? (
                <div className="flex items-center gap-1">
                  <span
                    className={cn(
                      "text-xs font-medium",
                      trend.isPositive ? "text-emerald-500" : "text-rose-500"
                    )}
                  >
                    {trend.isPositive ? "+" : ""}
                    {trend.value}%
                  </span>
                  <span className="text-xs text-muted-foreground">{trend.label}</span>
                </div>
              ) : null}
            </div>
            <div className={cn("p-3 rounded-xl", colors.bg)}>
              <div className={colors.icon}>{icon}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-3 w-32" />
          </div>
          <Skeleton className="h-12 w-12 rounded-xl" />
        </div>
      </CardContent>
    </Card>
  );
}

export function UserStatsCards({ analytics, isLoading }: UserStatsCardsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  const churnRate = analytics?.retentionRates?.day30
    ? (100 - analytics.retentionRates.day30).toFixed(1)
    : "0";

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        title="Total Users"
        value={analytics?.totalUsers?.toLocaleString() ?? "0"}
        icon={<Users className="h-5 w-5" />}
        trend={{
          value: 12.5,
          label: "vs last month",
          isPositive: true,
        }}
        color="blue"
        delay={0}
      />
      <StatCard
        title="Pro Subscribers"
        value={analytics?.proUsers?.toLocaleString() ?? "0"}
        icon={<Crown className="h-5 w-5" />}
        trend={{
          value: analytics?.conversionRate ?? 0,
          label: "conversion rate",
          isPositive: true,
        }}
        color="amber"
        delay={0.1}
      />
      <StatCard
        title="New This Week"
        value={analytics?.newUsersWeek?.toLocaleString() ?? "0"}
        icon={<UserPlus className="h-5 w-5" />}
        color="emerald"
        delay={0.2}
      />
      <StatCard
        title="Churn Rate"
        value={`${churnRate}%`}
        icon={<TrendingDown className="h-5 w-5" />}
        trend={{
          value: -2.3,
          label: "vs last month",
          isPositive: true,
        }}
        color="rose"
        delay={0.3}
      />
    </div>
  );
}
